import React, { useCallback, useEffect, useState } from "react";
import {
  AgentMemory,
  MemoryConflictError,
  MemoryEntryType,
  MemoryEntry,
  fetchAgentMemory,
  forgetMemoryEntry,
  saveMemoryText,
} from "../../../../services/agentMemoryService";

// What the agent remembers about how the owner wants the work done (SGL-417 / SGL-115).
// Business facts are deliberately not here: they live in Business settings.
// SGL-576: one row in the agent form. View opens it as one short note; Edit turns the
// whole memory into one text box saved at once, instead of a card per entry.

const TYPE_LABELS: Record<MemoryEntryType, string> = {
  preference: "Preferences",
  correction: "Corrections",
  decision: "Decisions",
  open_item: "Open items",
};

const PREVIEW_COUNT = 3;
const MAX_LINES = 30;

type Mode = "closed" | "view" | "edit";

const lastUpdated = (dates: string[]): string => {
  const latest = dates.filter(Boolean).sort().pop();
  if (!latest) return "";
  const days = Math.round((Date.now() - new Date(`${latest}T00:00:00`).getTime()) / 86400000);
  if (days <= 0) return "Updated today";
  if (days === 1) return "Updated yesterday";
  return `Updated ${days} days ago`;
};

interface MemorySectionProps {
  customerId: string;
  agentId: string;
  disabled?: boolean;
}

const MemorySection: React.FC<MemorySectionProps> = ({ customerId, agentId, disabled = false }) => {
  const [memory, setMemory] = useState<AgentMemory | null>(null);
  const [error, setError] = useState<string>("");
  const [notice, setNotice] = useState<string>("");
  const [mode, setMode] = useState<Mode>("closed");
  const [draft, setDraft] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError("");
    try {
      setMemory(await fetchAgentMemory(customerId, agentId));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load memory.");
    }
  }, [customerId, agentId]);

  useEffect(() => {
    if (customerId && agentId) load();
  }, [customerId, agentId, load]);

  const startEdit = () => {
    setDraft(memory?.text ?? "");
    setNotice("");
    setError("");
    setMode("edit");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      setMemory(await saveMemoryText(customerId, agentId, draft, memory?.etag ?? null));
      setMode("view");
    } catch (e) {
      if (e instanceof MemoryConflictError) {
        setNotice("The agent saved something while you were editing. Reloaded: check and edit again.");
        await load();
        setMode("view");
      } else {
        setError(e instanceof Error ? e.message : "Couldn't save memory.");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (entry: MemoryEntry) => {
    setBusyId(entry.id);
    setError("");
    setNotice("");
    try {
      setMemory(await forgetMemoryEntry(customerId, agentId, entry.id, memory?.etag ?? null, "forget"));
    } catch (e) {
      if (e instanceof MemoryConflictError) {
        setNotice("The agent saved something just now. Reloaded: check and try again.");
        await load();
      } else {
        setError(e instanceof Error ? e.message : "Couldn't remove it.");
      }
    } finally {
      setBusyId(null);
    }
  };

  const entries = memory?.entries ?? [];
  const live = entries.filter((e) => !e.expired);
  const openItems = live.filter((e) => e.type === "open_item").length;
  const summary = [
    ...live
      .filter((e) => e.type !== "open_item")
      .slice(0, PREVIEW_COUNT)
      .map((e) => e.text),
    ...(openItems ? [`${openItems} open item${openItems === 1 ? "" : "s"}`] : []),
  ].join(" · ");
  const draftLines = draft.split("\n").filter((l) => /^\s*[-*]\s+\S/.test(l)).length;

  const badge = (count: number) => (
    <span className="text-xs text-brand-primary bg-gray-100 rounded-full px-2.5 py-0.5">
      {count} / {MAX_LINES} lines
    </span>
  );

  const banners = (
    <>
      {error && (
        <div className="mt-3 px-3 py-2 rounded bg-red-100 text-red-800 border border-red-300 text-sm">{error}</div>
      )}
      {notice && (
        <div className="mt-3 px-3 py-2 rounded bg-yellow-50 text-yellow-800 border border-yellow-300 text-sm">
          {notice}
        </div>
      )}
    </>
  );

  if (mode === "closed") {
    return (
      <div className="mb-6 bg-white border border-gray-300 rounded-xl px-6 py-5" data-testid="agent-memory-section">
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="text-gray-800 font-semibold">Memory</span>
              {memory && badge(live.length)}
            </div>
            <p className="text-sm text-gray-500 mt-1.5 truncate">
              {!memory && !error
                ? "Loading memory…"
                : summary || "Nothing yet. Tell the agent how you like things done and it remembers."}
            </p>
            {memory && live.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">{lastUpdated(live.map((e) => e.date))}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setMode("view")}
            disabled={!memory}
            className="shrink-0 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
          >
            View
          </button>
        </div>
        {banners}
      </div>
    );
  }

  if (mode === "edit") {
    return (
      <div className="mb-6 bg-white border border-gray-300 rounded-xl px-7 py-6" data-testid="agent-memory-section">
        <div className="flex items-center gap-2.5 mb-3">
          <label htmlFor="agent-memory-text" className="flex-1 text-gray-800 font-semibold text-lg">
            Edit memory
          </label>
          {badge(draftLines)}
        </div>
        <textarea
          id="agent-memory-text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={14}
          placeholder={"Preferences\n- Never use emojis\n\nCorrections\n- …"}
          className="w-full border border-gray-300 rounded-lg px-4 py-3 font-mono text-sm leading-relaxed text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-primary"
        />
        <p className="text-xs text-gray-500 mt-2">
          Keep the four headings: Preferences, Corrections, Decisions, Open items. One line per item, starting with
          "-".
        </p>
        {banners}
        <div className="flex items-center gap-2 mt-4">
          <span className="flex-1 text-sm text-gray-500">Applies from your next message.</span>
          <button
            type="button"
            onClick={() => setMode("view")}
            className="px-4 py-2.5 rounded-lg text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || disabled || draftLines > MAX_LINES}
            className="px-5 py-2.5 bg-brand-primary text-white rounded-lg text-sm font-semibold hover:bg-brand-primary-hover disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-white border border-gray-300 rounded-xl px-7 py-6" data-testid="agent-memory-section">
      <div className="flex items-center gap-2.5">
        <span className="flex-1 text-gray-800 font-semibold text-lg">Memory</span>
        {badge(live.length)}
        <button
          type="button"
          onClick={startEdit}
          disabled={disabled}
          className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
        >
          Edit
        </button>
        <button type="button" onClick={() => setMode("closed")} className="px-2 py-2.5 text-sm hover:underline">
          Close
        </button>
      </div>
      <p className="text-sm text-gray-500 mt-2">
        How you want things done. The agent updates this after each chat; you can edit it any time. Facts about your
        business belong in Business settings.
      </p>
      {banners}
      {entries.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500 italic">
          Nothing yet. Tell the agent how you like things done (for example "never use emojis") and it will show up
          here.
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-7 gap-y-5 text-sm leading-relaxed">
          {(Object.keys(TYPE_LABELS) as MemoryEntryType[]).map((type) => {
            const group = entries.filter((e) => e.type === type);
            if (group.length === 0) return null;
            return (
              <div key={type}>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  {TYPE_LABELS[type]}
                </div>
                {group.map((entry) => (
                  <div key={entry.id} className="group flex items-baseline gap-2">
                    <span
                      className={`flex-1 min-w-0 ${entry.expired ? "text-gray-400 line-through" : "text-gray-800"}`}
                    >
                      {entry.text}
                    </span>
                    <button
                      type="button"
                      onClick={() => remove(entry)}
                      disabled={disabled || busyId === entry.id}
                      aria-label={`Remove: ${entry.text}`}
                      className="shrink-0 text-xs text-red-600 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:underline disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MemorySection;
